import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  Collaborator,
  CollaboratorContractType,
  CollaboratorGender,
  CollaboratorJobDescription,
  CollaboratorRemunerationType,
  MobileDeviceDetail,
  Pro20Page,
} from '../types/collaborator.types';

function parseNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }

  return fallback;
}

function readString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
}

function normalizeMobileDevice(raw: unknown): MobileDeviceDetail | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;

  return {
    uniquePhoneIdentifier: readString(item.uniquePhoneIdentifier),
    platform: readString(item.platform),
    model: readString(item.model),
    system: readString(item.system),
    machine: readString(item.machine),
    release: readString(item.release),
    systemVersion: readString(item.system_version ?? item.systemVersion),
    fcmToken: readString(item.fcmToken),
    registrationDate: readString(item.registrationDate),
  };
}

export function normalizeCollaborator(raw: unknown): Collaborator | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const employeeId = parseNumber(item.employeeId);

  if (!employeeId) {
    return null;
  }

  const devices = Array.isArray(item.mobileDeviceDetails)
    ? item.mobileDeviceDetails
        .map((device) => normalizeMobileDevice(device))
        .filter((device): device is MobileDeviceDetail => device !== null)
    : [];

  return {
    employeeId,
    branchCode: readString(item.branchCode),
    firstName: readString(item.firstName),
    lastName: readString(item.lastName),
    email: readString(item.email),
    phone: readString(item.phone),
    prefix: readString(item.prefix),
    userName: readString(item.userName),
    password: readString(item.password),
    gender: readString(item.gender) as CollaboratorGender,
    dob: readString(item.dob),
    jobDescription: readString(item.jobDescription) as CollaboratorJobDescription,
    contractType: readString(item.contractType) as CollaboratorContractType,
    remunerationType: readString(
      item.remunerationType,
    ) as CollaboratorRemunerationType,
    retribution: parseNumber(item.retribution),
    startDateInduction: readString(item.startDateInduction),
    endDateInduction: readString(item.endDateInduction),
    mobileDeviceDetails: devices,
    fired: parseBoolean(item.fired),
    visible: parseBoolean(item.visible, true),
  };
}

export function normalizeCollaboratorsList(raw: unknown): Collaborator[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizeCollaborator(item))
    .filter((item): item is Collaborator => item !== null);
}

function isPro20Page<T>(value: unknown): value is Pro20Page<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'content' in value &&
    Array.isArray((value as Pro20Page<T>).content)
  );
}

/** Normalizes PRO20 collaborator results into a paginated shape for the UI */
export function normalizeCollaboratorsResponse(
  results: unknown,
  fallbackSize = 20,
  options?: { page?: number; size?: number; search?: string },
): PaginatedResponse<Collaborator> {
  let list: Collaborator[] = [];

  if (isPro20Page<unknown>(results)) {
    list = normalizeCollaboratorsList(results.content);
    return {
      data: list,
      meta: {
        page: results.number + 1,
        limit: results.size,
        total: results.totalElements,
        totalPages: Math.max(results.totalPages, 1),
      },
    };
  }

  list = normalizeCollaboratorsList(results);

  const search = options?.search?.trim().toLowerCase();
  if (search) {
    list = list.filter((row) => {
      const haystack = [
        row.firstName,
        row.lastName,
        row.email,
        row.phone,
        row.jobDescription,
        row.userName,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }

  const size = options?.size ?? fallbackSize;
  const pageIndex = options?.page ?? 0;
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / size) || 1);
  const start = pageIndex * size;
  const data = list.slice(start, start + size);

  return {
    data,
    meta: {
      page: pageIndex + 1,
      limit: size,
      total,
      totalPages,
    },
  };
}
